/**
 * AstorPrefsTab — fourth tab of the consumer Profile page. Two cards in a
 * single column (maxWidth 720):
 *
 *   1. "How Astor works for you" — AstorAvatar + heading + subtitle + 5
 *      AstorPrefToggle rows (Auto-fill / Draft replies / Pre-stage /
 *      Negotiation coaching / Surface refi opps).
 *   2. "What Astor can see" — privacy prose + two ghost buttons
 *      (View Astor's audit log / Clear conversation history).
 *
 * Auto-save: each toggle calls `onSavePref(key, value)`. The consumer-side
 * Astor preferences schema does not exist yet (Phase-4 ISSUE flag #4 in the
 * design spec), so the wire-up is staged with a TODO that stores into the
 * profile's `astorPreferences` map via `useUpdateProfile`. The instant we
 * stand up a typed `astorPrefs.*` endpoint we swap the storage location
 * without touching the UI.
 */

import { Box, GlassAlert, GlassButton, GlassCard, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useProfile, useUpdateProfile } from '../../hooks';
import { AstorAvatar } from '../loans/AstorAvatar';
import AstorPrefToggle from './AstorPrefToggle';

type AstorPrefKey = 'autoFill' | 'draftReplies' | 'prestage' | 'negotiation' | 'refi';

const TOGGLES: Array<{ key: AstorPrefKey; labelKey: string; subKey: string }> = [
  {
    key: 'autoFill',
    labelKey: 'consumer.profile.astorPrefs.autoFill',
    subKey: 'consumer.profile.astorPrefs.autoFillSub',
  },
  {
    key: 'draftReplies',
    labelKey: 'consumer.profile.astorPrefs.draftReplies',
    subKey: 'consumer.profile.astorPrefs.draftRepliesSub',
  },
  {
    key: 'prestage',
    labelKey: 'consumer.profile.astorPrefs.prestage',
    subKey: 'consumer.profile.astorPrefs.prestageSub',
  },
  {
    key: 'negotiation',
    labelKey: 'consumer.profile.astorPrefs.negotiation',
    subKey: 'consumer.profile.astorPrefs.negotiationSub',
  },
  { key: 'refi', labelKey: 'consumer.profile.astorPrefs.refi', subKey: 'consumer.profile.astorPrefs.refiSub' },
];

// P4: design spec ships all 5 toggles ON by default — the borrower portal
// is opt-out for Astor assists, not opt-in. Users toggle individual prefs
// off if they want a quieter experience.
const DEFAULT_PREFS: Record<AstorPrefKey, boolean> = {
  autoFill: true,
  draftReplies: true,
  prestage: true,
  negotiation: true,
  refi: true,
};

export function AstorPrefsTab(): ReactElement {
  const { t } = useI18n();
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // TODO(consumer-profile #4): replace `profile.profile.astorPreferences` blob
  // with a typed `astorPrefs.getMine` / `astorPrefs.updateMine` tRPC endpoint
  // once schema lands.
  const storedPrefs = useMemo(() => {
    const raw = (profile?.profile as Record<string, unknown> | undefined)?.astorPreferences as
      | Partial<Record<AstorPrefKey, boolean>>
      | undefined;
    return { ...DEFAULT_PREFS, ...(raw ?? {}) };
  }, [profile]);

  const handleToggle = useCallback(
    async (key: AstorPrefKey, value: boolean) => {
      const existingProfile = (profile?.profile as Record<string, unknown>) ?? {};
      const existingAstor =
        (existingProfile.astorPreferences as Record<AstorPrefKey, boolean> | undefined) ?? DEFAULT_PREFS;
      try {
        await updateProfile.mutateAsync({
          profile: {
            ...existingProfile,
            astorPreferences: { ...existingAstor, [key]: value },
          },
        });
        setSavedFlash(t('notification.preferences.saved'));
        setTimeout(() => setSavedFlash(null), 2500);
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: surface in browser telemetry
        console.error('Failed to update Astor preference:', err);
      }
    },
    [profile, updateProfile, t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, maxWidth: 720, mx: 'auto' }}>
      {savedFlash && <GlassAlert severity="success">{savedFlash}</GlassAlert>}
      {updateProfile.error && <GlassAlert severity="error">{updateProfile.error.message}</GlassAlert>}

      {/* ───── How Astor works for you ───── */}
      <GlassCard sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
          <AstorAvatar size={28} />
          {/* P3: match sibling card-section headings (overline + tracked
              uppercase) per the design-pass spec — keeps the card hierarchy
              consistent with the privacy heading directly below. */}
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ display: 'block', letterSpacing: '0.08em', fontWeight: 600 }}
          >
            {t('consumer.profile.astorPrefs.heading')}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          {t('consumer.profile.astorPrefs.subtitle')}
        </Typography>
        {TOGGLES.map((toggle, idx) => (
          <AstorPrefToggle
            key={toggle.key}
            label={t(toggle.labelKey)}
            subText={t(toggle.subKey)}
            checked={storedPrefs[toggle.key]}
            onChange={(v) => handleToggle(toggle.key, v)}
            disabled={updateProfile.isPending}
            isLast={idx === TOGGLES.length - 1}
          />
        ))}
      </GlassCard>

      {/* ───── What Astor can see (privacy) ───── */}
      <GlassCard sx={{ p: 2 }}>
        <Typography variant="overline" color="textSecondary" sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}>
          {t('consumer.profile.astorPrefs.privacyHeading')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          {t('consumer.profile.astorPrefs.privacyBody')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <GlassButton
            variant="outlined"
            onClick={() => {
              // TODO(consumer-profile #5): wire to consumer-scoped Astor audit
              // log endpoint (privacy/visibility view of agent reads/writes).
            }}
          >
            {t('consumer.profile.astorPrefs.viewAuditLog')}
          </GlassButton>
          <GlassButton
            variant="outlined"
            color="error"
            onClick={() => {
              // TODO(consumer-profile #5): wire to "clear conversation history"
              // endpoint — destructive, may need a confirm Dialog before call.
            }}
          >
            {t('consumer.profile.astorPrefs.clearHistory')}
          </GlassButton>
        </Box>
      </GlassCard>
    </Box>
  );
}

export default AstorPrefsTab;
